<template>
  <div
    class="flex flex-col gap-[48px]"
    :class="{ invisible: phase === 'measure' }"
  >
    <div
      v-for="(page, pageIdx) in pages"
      :key="pageIdx"
      data-a4-page
      class="relative flex h-[1754px] w-[1240px] bg-white"
      style="-webkit-print-color-adjust: exact"
    >
      <!-- Left sidebar -->
      <div class="h-full w-[330px] shrink-0 bg-[#f3f3f5] px-[48px] pt-[48px]">
        <!-- Photo (page 1 only) -->
        <div
          v-if="hasPhoto && pageIdx === 0"
          class="h-[256px] w-[254px] overflow-hidden bg-gray-200"
        >
          <img
            v-if="profileImageUrl"
            :src="profileImageUrl"
            class="h-full w-full object-cover"
          />
          <image-preview
            v-else
            :uuid="photoUuid"
            class="h-full w-full object-cover"
          />
        </div>

        <!-- Name + cyan underline -->
        <div :class="hasPhoto && pageIdx === 0 ? 'mt-[48px]' : ''">
          <p class="text-[24px] leading-[36px] text-[#1d1d1f]">
            {{ account.firstName }} {{ account.lastName }}
          </p>
          <div class="mt-[16px] h-[6px] w-[200px] bg-[#00f9ff]" />
        </div>

        <!-- Geboren (page 1 only) -->
        <template v-if="pageIdx === 0">
          <div class="mt-[48px]">
            <p
              class="text-[21px] font-bold uppercase leading-[32px] text-[#1d1d1f]"
            >
              Geboren
            </p>
            <div class="mt-[6px] flex flex-col gap-[6px]">
              <div v-if="account.birthday" class="flex items-center gap-[9px]">
                <svg-icon
                  name="birthday-cake"
                  class="h-[25px] w-[25px] shrink-0 text-[#1d1d1f]"
                />
                <span class="text-[24px] leading-[36px] text-[#1d1d1f]">
                  {{ formatDate(account.birthday) }}
                </span>
              </div>
              <p
                v-if="account.placeOfBirth"
                class="text-[24px] leading-[36px] text-[#1d1d1f]"
              >
                {{ account.placeOfBirth }}
              </p>
            </div>
          </div>

          <!-- Kontakt -->
          <div class="mt-[48px]">
            <p
              class="text-[21px] font-bold uppercase leading-[32px] text-[#1d1d1f]"
            >
              Kontakt
            </p>
            <div class="mt-[6px] flex flex-col gap-[6px]">
              <div v-if="account.phone" class="flex items-start gap-[9px]">
                <svg-icon
                  name="phone"
                  class="mt-[6px] h-[25px] w-[25px] shrink-0 text-[#1d1d1f]"
                />
                <span
                  class="min-w-0 break-words text-[24px] leading-[36px] text-[#1d1d1f]"
                >
                  {{ account.phone }}
                </span>
              </div>
              <div v-if="account.email" class="flex items-start gap-[9px]">
                <svg-icon
                  name="envelope"
                  class="mt-[6px] h-[25px] w-[25px] shrink-0 text-[#1d1d1f]"
                />
                <span
                  class="min-w-0 break-words text-[24px] leading-[36px] text-[#1d1d1f]"
                >
                  {{ account.email }}
                </span>
              </div>
              <div v-if="hasShippingAddress" class="flex items-start gap-[9px]">
                <svg-icon
                  name="map-marker"
                  class="mt-[6px] h-[25px] w-[25px] shrink-0 text-[#1d1d1f]"
                />
                <div class="min-w-0 text-[24px] leading-[36px] text-[#1d1d1f]">
                  <p v-if="account.shippingStreet" class="m-0">
                    {{ account.shippingStreet }}
                  </p>
                  <p v-if="addressCityLine" class="m-0">
                    {{ addressCityLine }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>

      <!-- Right content area -->
      <div class="flex-1 px-[50px] pt-[47px]">
        <!-- Lebenslauf heading + cyan underline (page 1 only) -->
        <div v-if="pageIdx === 0" :ref="trackHeading">
          <p
            class="text-[54px] font-bold leading-[65px] tracking-[-1.1px] text-[#1d1d1f]"
          >
            Lebenslauf
          </p>
          <div class="mt-[24px] h-[6px] w-[200px] bg-[#b8fdff]" />
        </div>

        <!-- Sections -->
        <div
          :class="pageIdx === 0 ? 'mt-[48px]' : ''"
          class="flex flex-col gap-[48px]"
        >
          <div
            v-for="(block, blockIdx) in page.blocks"
            :key="blockIdx"
            :ref="(el: any) => trackSection(el, blockIdx)"
          >
            <!-- Praktikum -->
            <template v-if="block.type === 'praktikum'">
              <div class="flex items-center gap-[12px]">
                <svg-icon
                  name="calendar"
                  class="h-[31px] w-[31px] shrink-0 text-[#1d1d1f]"
                />
                <p
                  class="text-[36px] font-bold leading-[43px] tracking-[-0.72px] text-[#1d1d1f]"
                >
                  BERUFSERFAHRUNG
                </p>
              </div>
              <div class="mt-[24px] flex flex-col gap-[24px] pl-[30px]">
                <div
                  v-for="(exp, i) in block.entries"
                  :key="i"
                  :ref="(el: any) => trackEntry(el, blockIdx, i)"
                >
                  <p class="text-[24px] leading-[36px] text-[#1d1d1f]">
                    {{ formatDateRange(exp.from, exp.to) }}
                  </p>
                  <p class="text-[24px] leading-[36px] text-[#1d1d1f]">
                    {{ exp.companyName }}
                  </p>
                  <p
                    v-if="jobTitle(exp)"
                    class="text-[24px] font-bold leading-[36px] text-[#1d1d1f]"
                  >
                    {{ jobTitle(exp) }}
                  </p>
                  <template
                    v-for="(descBlock, di) in parseDescriptionBlocks(
                      isDescriptionEnabled ? exp.description : null,
                    )"
                    :key="`desc-${di}`"
                  >
                    <ul
                      v-if="descBlock.type === 'list'"
                      class="list-disc pl-[30px] text-[24px] leading-[36px] text-[#1d1d1f]"
                    >
                      <li v-for="(item, ii) in descBlock.items" :key="ii">
                        {{ item }}
                      </li>
                    </ul>
                    <p
                      v-else
                      class="whitespace-pre-line text-[24px] leading-[36px] text-[#1d1d1f]"
                    >
                      {{ descBlock.items.join('\n') }}
                    </p>
                  </template>
                </div>
              </div>
            </template>

            <!-- Praktikum continuation (no heading) -->
            <template v-else-if="block.type === 'praktikum-cont'">
              <div class="flex flex-col gap-[24px] pl-[30px]">
                <div v-for="(exp, i) in block.entries" :key="i">
                  <p class="text-[24px] leading-[36px] text-[#1d1d1f]">
                    {{ formatDateRange(exp.from, exp.to) }}
                  </p>
                  <p class="text-[24px] leading-[36px] text-[#1d1d1f]">
                    {{ exp.companyName }}
                  </p>
                  <p
                    v-if="jobTitle(exp)"
                    class="text-[24px] font-bold leading-[36px] text-[#1d1d1f]"
                  >
                    {{ jobTitle(exp) }}
                  </p>
                  <template
                    v-for="(descBlock, di) in parseDescriptionBlocks(
                      isDescriptionEnabled ? exp.description : null,
                    )"
                    :key="`desc-${di}`"
                  >
                    <ul
                      v-if="descBlock.type === 'list'"
                      class="list-disc pl-[30px] text-[24px] leading-[36px] text-[#1d1d1f]"
                    >
                      <li v-for="(item, ii) in descBlock.items" :key="ii">
                        {{ item }}
                      </li>
                    </ul>
                    <p
                      v-else
                      class="whitespace-pre-line text-[24px] leading-[36px] text-[#1d1d1f]"
                    >
                      {{ descBlock.items.join('\n') }}
                    </p>
                  </template>
                </div>
              </div>
            </template>

            <!-- Bildungsweg -->
            <template v-else-if="block.type === 'bildungsweg'">
              <div class="flex items-center gap-[12px]">
                <svg-icon
                  name="medal"
                  class="h-[31px] w-[31px] shrink-0 text-[#1d1d1f]"
                />
                <p
                  class="text-[36px] font-bold leading-[43px] tracking-[-0.72px] text-[#1d1d1f]"
                >
                  BILDUNGSWEG
                </p>
              </div>
              <div class="mt-[24px] flex flex-col gap-[24px] pl-[30px]">
                <div
                  v-for="(entry, i) in block.entries"
                  :key="i"
                  :ref="(el: any) => trackEntry(el, blockIdx, i)"
                >
                  <p class="text-[24px] leading-[36px] text-[#1d1d1f]">
                    {{ formatDateRange(entry.from, entry.to) }}
                  </p>
                  <p class="text-[24px] leading-[36px] text-[#1d1d1f]">
                    {{ entry.graduation }}, {{ entry.placeName }}
                  </p>
                  <p
                    v-if="entry.courseName"
                    class="text-[24px] font-bold leading-[36px] text-[#1d1d1f]"
                  >
                    Studiengang {{ entry.courseName }}
                  </p>
                </div>
              </div>
            </template>

            <!-- Bildungsweg continuation (no heading) -->
            <template v-else-if="block.type === 'bildungsweg-cont'">
              <div class="flex flex-col gap-[24px] pl-[30px]">
                <div v-for="(entry, i) in block.entries" :key="i">
                  <p class="text-[24px] leading-[36px] text-[#1d1d1f]">
                    {{ formatDateRange(entry.from, entry.to) }}
                  </p>
                  <p class="text-[24px] leading-[36px] text-[#1d1d1f]">
                    {{ entry.graduation }}, {{ entry.placeName }}
                  </p>
                  <p
                    v-if="entry.courseName"
                    class="text-[24px] font-bold leading-[36px] text-[#1d1d1f]"
                  >
                    Studiengang {{ entry.courseName }}
                  </p>
                </div>
              </div>
            </template>

            <!-- Sprachen -->
            <template v-else-if="block.type === 'sprachen'">
              <div class="flex items-center gap-[12px]">
                <svg-icon
                  name="translate"
                  class="h-[31px] w-[31px] shrink-0 text-[#1d1d1f]"
                />
                <p
                  class="text-[36px] font-bold leading-[43px] tracking-[-0.72px] text-[#1d1d1f]"
                >
                  SPRACHEN
                </p>
              </div>
              <div class="mt-[24px] flex flex-col gap-[6px] pl-[24px]">
                <div
                  v-for="(lang, i) in block.entries"
                  :key="i"
                  class="flex items-center gap-[12px]"
                >
                  <span
                    class="shrink-0 text-[21px] font-bold leading-[30px] text-[#1d1d1f]"
                    >{{ languageName(lang.code) }}</span
                  >
                  <div
                    class="h-[9px] flex-1 overflow-hidden rounded-full bg-[#f3f3f5]"
                  >
                    <div
                      class="h-full rounded-full bg-[#00f9ff]"
                      :style="{
                        width: languageLevelPercent(lang.level) + '%',
                      }"
                    />
                  </div>
                </div>
              </div>
            </template>

            <!-- Mobilität -->
            <template v-else-if="block.type === 'mobilitaet'">
              <div class="flex items-center gap-[12px]">
                <svg-icon
                  name="car-side"
                  class="h-[31px] w-[31px] shrink-0 text-[#1d1d1f]"
                />
                <p
                  class="text-[36px] font-bold leading-[43px] tracking-[-0.72px] text-[#1d1d1f]"
                >
                  MOBILITÄT
                </p>
              </div>
              <div class="mt-[24px] flex flex-col gap-[6px] pl-[24px]">
                <div
                  v-if="mobility.hasDriversLicense !== null"
                  class="flex items-center gap-[18px]"
                >
                  <span
                    class="w-[130px] shrink-0 text-[21px] font-bold leading-[30px] text-[#1d1d1f]"
                    >Führerschein</span
                  >
                  <span class="text-[21px] leading-[30px] text-[#1d1d1f]">{{
                    mobility.hasDriversLicense ? 'Klasse B' : 'Nicht vorhanden'
                  }}</span>
                </div>
                <div
                  v-if="mobility.hasCar !== null"
                  class="flex items-center gap-[18px]"
                >
                  <span
                    class="w-[130px] shrink-0 text-[21px] font-bold leading-[30px] text-[#1d1d1f]"
                    >Auto</span
                  >
                  <span class="text-[21px] leading-[30px] text-[#1d1d1f]">{{
                    mobility.hasCar ? 'Vorhanden' : 'Nicht vorhanden'
                  }}</span>
                </div>
              </div>
            </template>

            <!-- Hard Skills -->
            <template v-else-if="block.type === 'hard-skills'">
              <div class="flex items-center gap-[12px]">
                <svg-icon
                  name="display"
                  class="h-[31px] w-[31px] shrink-0 text-[#1d1d1f]"
                />
                <p
                  class="text-[36px] font-bold leading-[43px] tracking-[-0.72px] text-[#1d1d1f]"
                >
                  HARD SKILLS
                </p>
              </div>
              <div class="mt-[24px] flex flex-col gap-[6px] pl-[24px]">
                <div
                  v-for="(skill, i) in block.entries"
                  :key="i"
                  class="flex items-center gap-[12px]"
                >
                  <span
                    class="shrink-0 text-[21px] font-bold leading-[30px] text-[#1d1d1f]"
                    >{{ skill.name }}</span
                  >
                  <div
                    class="h-[9px] flex-1 overflow-hidden rounded-full bg-[#f3f3f5]"
                  >
                    <div
                      class="h-full rounded-full bg-[#00f9ff]"
                      :style="{
                        width: skillLevelPercent(skill.level) + '%',
                      }"
                    />
                  </div>
                </div>
              </div>
            </template>

            <!-- Soft Skills -->
            <template v-else-if="block.type === 'soft-skills'">
              <div class="flex items-center gap-[12px]">
                <svg-icon
                  name="brain"
                  class="h-[31px] w-[31px] shrink-0 text-[#1d1d1f]"
                />
                <p
                  class="text-[36px] font-bold leading-[43px] tracking-[-0.72px] text-[#1d1d1f]"
                >
                  SOFT SKILLS
                </p>
              </div>
              <div class="mt-[24px] flex flex-col gap-[6px] pl-[24px]">
                <span
                  v-for="(skill, i) in block.entries"
                  :key="i"
                  class="text-[21px] font-bold leading-[30px] text-[#1d1d1f]"
                >
                  {{ skill.name }}
                </span>
              </div>
            </template>

            <!-- Hobbies -->
            <template v-else-if="block.type === 'hobbies'">
              <div class="flex items-center gap-[12px]">
                <svg-icon
                  name="heart"
                  class="h-[31px] w-[31px] shrink-0 text-[#1d1d1f]"
                />
                <p
                  class="text-[36px] font-bold leading-[43px] tracking-[-0.72px] text-[#1d1d1f]"
                >
                  HOBBYS
                </p>
              </div>
              <div class="mt-[24px] flex flex-col gap-[6px] pl-[24px]">
                <span
                  v-for="(hobby, i) in block.entries"
                  :key="i"
                  class="text-[21px] font-bold leading-[30px] text-[#1d1d1f]"
                >
                  {{ hobby.name }}
                </span>
              </div>
            </template>
          </div>
        </div>

        <!-- Page number -->
        <p
          class="absolute bottom-[40px] right-[48px] text-[22px] leading-[31px] text-[#1d1d1f]"
        >
          {{ pageIdx + 2 }} | {{ totalDocPages }} {{ account.firstName }}
          {{ account.lastName }} | Lebenslauf
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, watch, nextTick } from 'vue'
import { format, parseISO } from 'date-fns'
import { de } from 'date-fns/locale'
import ImagePreview from '@/components/ImagePreview.vue'
import SvgIcon from '@/components/SvgIcon.vue'
import accountStore from '@/stores/account.store'
import documentsStore from '@/stores/documents.store'
import mobilityStore from '@/stores/mobility.store'
import { DocumentType } from '@/enums'
import { languageOptions } from '@/assets/data/languages'
import {
  collectEducationEntries,
  sortEducationEntriesByLatestTo,
} from '@/units/ds/utils/education-entries'
import { parseDescriptionBlocks } from '@/units/ds/utils/parse-description-blocks'
import { Features, FeatureFlags } from '@/modules/features'

const props = withDefaults(
  defineProps<{
    totalDocPages?: number
    profileImageUrl?: string
  }>(),
  { totalDocPages: 3, profileImageUrl: undefined },
)

const account = computed(() => accountStore.state.account)

const isDescriptionEnabled = computed(() =>
  Features.isEnabled(FeatureFlags.WorkExperienceDescription),
)

const addressCityLine = computed(() =>
  [account.value.shippingPostalCode, account.value.shippingCity]
    .filter(Boolean)
    .join(' '),
)

const hasShippingAddress = computed(
  () => Boolean(account.value.shippingStreet) || Boolean(addressCityLine.value),
)

const mobility = computed(() => mobilityStore.state.mobility)

mobilityStore.fetchMobility()

const photoUuid = computed(() => {
  const pics = documentsStore.getDocumentsByCategory(DocumentType.Picture)
  return pics.length > 0 ? pics[0].uuid : null
})

const hasPhoto = computed(() => !!props.profileImageUrl || !!photoUuid.value)

const practicalExperiences = computed(
  () => account.value.practicalExperiences ?? [],
)
const educationEntries = computed(() =>
  sortEducationEntriesByLatestTo(collectEducationEntries(account.value)),
)
const languageSkills = computed(() => account.value.languageSkills ?? [])
const hardSkills = computed(() =>
  (account.value.hardSkills ?? []).filter((s) => s.category === 'software'),
)
const softSkills = computed(() => account.value.softSkills ?? [])
const hobbies = computed(() => account.value.hobbies ?? [])

// --- DOM-measured pagination ---

const PAGE_HEIGHT = 1754
const TOP_PADDING = 47
const FOOTER_RESERVED = 80
const CONTENT_AREA = PAGE_HEIGHT - TOP_PADDING - FOOTER_RESERVED
const SECTION_GAP = 48
const HEADING_MARGIN = 48
const ENTRY_GAP = 24

const splittableTypes = [
  'praktikum',
  'bildungsweg',
  'praktikum-cont',
  'bildungsweg-cont',
]

type ContentBlock = {
  type: string
  entries: readonly any[]
  height: number
  headerHeight?: number
  entryHeights?: number[]
}

type Page = { blocks: ContentBlock[] }

// Two-phase rendering: 'measure' renders all blocks on one page so we can
// read their real offsetHeight; 'ready' uses the measured values to paginate.
const phase = ref<'measure' | 'ready'>('measure')

let headingElement: HTMLElement | null = null
const sectionElMap = new Map<number, HTMLElement>()
const entryElMap = new Map<number, HTMLElement[]>()

const measuredHeadingH = ref(0)
const measuredSections = ref<number[]>([])
const measuredEntries = ref<Map<number, number[]>>(new Map())

const trackHeading = (el: any) => {
  headingElement = el instanceof HTMLElement ? el : null
}

const trackSection = (el: any, blockIdx: number) => {
  if (phase.value !== 'measure' || !(el instanceof HTMLElement)) return
  sectionElMap.set(blockIdx, el)
}

const trackEntry = (el: any, blockIdx: number, entryIdx: number) => {
  if (phase.value !== 'measure' || !(el instanceof HTMLElement)) return
  if (!entryElMap.has(blockIdx)) entryElMap.set(blockIdx, [])
  entryElMap.get(blockIdx)![entryIdx] = el
}

const buildRawBlocks = (): ContentBlock[] => {
  const blocks: ContentBlock[] = []

  if (practicalExperiences.value.length > 0) {
    blocks.push({
      type: 'praktikum',
      entries: practicalExperiences.value,
      height: 0,
    })
  }

  if (educationEntries.value.length > 0) {
    blocks.push({
      type: 'bildungsweg',
      entries: educationEntries.value,
      height: 0,
    })
  }

  if (languageSkills.value.length > 0) {
    blocks.push({
      type: 'sprachen',
      entries: languageSkills.value,
      height: 0,
    })
  }

  if (
    mobility.value.hasDriversLicense !== null ||
    mobility.value.hasCar !== null
  ) {
    blocks.push({ type: 'mobilitaet', entries: [], height: 0 })
  }

  if (hardSkills.value.length > 0) {
    blocks.push({ type: 'hard-skills', entries: hardSkills.value, height: 0 })
  }

  if (softSkills.value.length > 0) {
    blocks.push({ type: 'soft-skills', entries: softSkills.value, height: 0 })
  }

  if (hobbies.value.length > 0) {
    blocks.push({ type: 'hobbies', entries: hobbies.value, height: 0 })
  }

  return blocks
}

const rawBlocks = computed(() => buildRawBlocks())

const availableHeight = (pageIdx: number) =>
  CONTENT_AREA - (pageIdx === 0 ? measuredHeadingH.value + HEADING_MARGIN : 0)

const splitBlock = (
  block: ContentBlock,
  remaining: number,
): { fit: ContentBlock | null; overflow: ContentBlock | null } => {
  const eHeights = block.entryHeights
  const headerH = block.headerHeight ?? 0

  if (!eHeights || eHeights.length === 0) {
    return { fit: null, overflow: block }
  }

  if (remaining < headerH + eHeights[0]) {
    return { fit: null, overflow: block }
  }

  let used = headerH
  let fitCount = 0
  for (let i = 0; i < eHeights.length; i++) {
    const gap = i > 0 ? ENTRY_GAP : 0
    if (used + gap + eHeights[i] > remaining) break
    used += gap + eHeights[i]
    fitCount++
  }

  if (fitCount === 0) return { fit: null, overflow: block }
  if (fitCount === block.entries.length) return { fit: block, overflow: null }

  const baseType = block.type.replace('-cont', '')
  const overflowEntryHeights = eHeights.slice(fitCount)
  const overflowHeight =
    overflowEntryHeights.reduce((sum, h) => sum + h, 0) +
    Math.max(0, overflowEntryHeights.length - 1) * ENTRY_GAP

  return {
    fit: {
      type: block.type,
      entries: block.entries.slice(0, fitCount),
      height: used,
      entryHeights: eHeights.slice(0, fitCount),
      headerHeight: headerH,
    },
    overflow: {
      type: `${baseType}-cont`,
      entries: block.entries.slice(fitCount),
      height: overflowHeight,
      entryHeights: overflowEntryHeights,
      headerHeight: 0,
    },
  }
}

const paginateBlocks = (blocks: ContentBlock[]): Page[] => {
  const result: Page[] = []
  let currentPage: ContentBlock[] = []
  let pageIdx = 0
  let usedHeight = 0

  const startNewPage = () => {
    if (currentPage.length > 0) result.push({ blocks: currentPage })
    currentPage = []
    pageIdx = result.length
    usedHeight = 0
  }

  const allBlocks = [...blocks]
  let i = 0
  while (i < allBlocks.length) {
    const block = allBlocks[i]
    const available = availableHeight(pageIdx) - usedHeight
    const blockWithGap =
      block.height + (currentPage.length > 0 ? SECTION_GAP : 0)

    if (blockWithGap <= available) {
      usedHeight += blockWithGap
      currentPage.push(block)
      i++
    } else if (splittableTypes.includes(block.type)) {
      const effectiveRemaining =
        available - (currentPage.length > 0 ? SECTION_GAP : 0)
      const { fit, overflow } = splitBlock(block, effectiveRemaining)
      if (fit) {
        usedHeight += fit.height + (currentPage.length > 0 ? SECTION_GAP : 0)
        currentPage.push(fit)
      }
      startNewPage()
      if (overflow) {
        allBlocks[i] = overflow
      } else {
        i++
      }
    } else {
      startNewPage()
    }
  }

  if (currentPage.length > 0) result.push({ blocks: currentPage })
  return result.length > 0 ? result : [{ blocks: [] }]
}

const pages = computed<Page[]>(() => {
  const blocks = rawBlocks.value

  if (phase.value === 'measure') {
    return [{ blocks: blocks.map((b) => ({ ...b, height: 0 })) }]
  }

  const measured: ContentBlock[] = blocks.map((b, i) => {
    const block: ContentBlock = {
      ...b,
      height: measuredSections.value[i] ?? 0,
    }
    if (splittableTypes.includes(b.type) && measuredEntries.value.has(i)) {
      const eH = measuredEntries.value.get(i)!
      block.entryHeights = eH
      const totalEntryArea =
        eH.reduce((sum, h) => sum + h, 0) +
        Math.max(0, eH.length - 1) * ENTRY_GAP
      block.headerHeight = Math.max(0, block.height - totalEntryArea)
    }
    return block
  })

  return paginateBlocks(measured)
})

const captureHeights = () => {
  measuredHeadingH.value = headingElement?.offsetHeight ?? 0

  const heights: number[] = []
  const entries = new Map<number, number[]>()

  for (let i = 0; i < rawBlocks.value.length; i++) {
    const el = sectionElMap.get(i)
    heights.push(el?.offsetHeight ?? 0)

    if (splittableTypes.includes(rawBlocks.value[i].type)) {
      const els = entryElMap.get(i) ?? []
      entries.set(
        i,
        els.map((e) => e?.offsetHeight ?? 0),
      )
    }
  }

  measuredSections.value = heights
  measuredEntries.value = entries
}

const runMeasureCycle = async () => {
  sectionElMap.clear()
  entryElMap.clear()
  phase.value = 'measure'
  await nextTick()
  captureHeights()
  phase.value = 'ready'
}

onMounted(async () => {
  // Refs are already populated from the initial render — just measure and switch.
  await nextTick()
  captureHeights()
  phase.value = 'ready'
})

watch(
  [
    practicalExperiences,
    educationEntries,
    languageSkills,
    hardSkills,
    softSkills,
    hobbies,
    mobility,
  ],
  () => runMeasureCycle(),
  { deep: true },
)

// --- Formatting helpers ---

const formatDate = (date: string) => {
  if (!date) return ''
  try {
    return format(parseISO(date), 'dd. MMMM yyyy', { locale: de })
  } catch {
    return date
  }
}

const formatDateRange = (from: string, to: string | null) => {
  const formatMonth = (d: string) => {
    try {
      return format(parseISO(d), 'MM/yyyy')
    } catch {
      return d
    }
  }
  return `${formatMonth(from)} - ${to ? formatMonth(to) : 'heute'}`
}

const jobTitle = (exp: { jobDescriptions?: string[] }) => {
  if (!exp.jobDescriptions?.length) return null
  const first = exp.jobDescriptions[0]
  const titleMatch = first.match(/^Title:\s*(.+)/)
  return titleMatch ? titleMatch[1] : first
}

const languageName = (code: string) => {
  const option = languageOptions.find((l) => l.code === code)
  return option?.language ?? code
}

const languageLevelPercent = (level: string | number) => {
  const map: Record<string, number> = {
    '1': 14,
    '2': 28,
    '3': 43,
    '4': 57,
    '5': 71,
    '6': 86,
    '7': 100,
  }
  return map[String(level)] ?? 50
}

const skillLevelPercent = (level: string) => {
  const map: Record<string, number> = {
    Grundkenntnisse: 33,
    'Erweiterte Kenntnisse': 66,
    'Fundierte Kenntnisse': 100,
  }
  return map[level] ?? 50
}

defineExpose({ pages, phase })
</script>
